import { useWidget } from '@telescope/cassini-hooks';
import { Box, Button, Heading } from '@telescope/cassini-ui';
import { GenericObject } from '@telescope/cassini-utilities';
import { Open } from 'generated-types/widget';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useAuth } from '../lib';
import { AuthMethod, InputName, renderInput } from '../utils';
import { FacebookButton } from './FacebookButton';
import { OptionsDivider } from './OptionsDivider';

interface RegisterFormStep1Props {
  onFormSubmit(formData: GenericObject): void;
}
export const RegisterFormStep1 = ({ onFormSubmit }: RegisterFormStep1Props) => {
  const { isRegistering } = useAuth();
  const { data } = useWidget<Open>({ select: (data) => data.widget.globalViews.auth });
  const formMethods = useForm<{ [x: string]: any }>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldUnregister: true,
  });
  const { handleSubmit, setValue, watch, register, getValues, trigger, setError } = formMethods;
  const content = data.content.registrationModal;
  const { isMethodEnabled, authMethods } = useAuth();
  const hasMultipleMethods = Object.values(authMethods).filter((method) => method).length > 1;
  const watchMethod = watch('method');

  // Save the authMethod as a form value
  useEffect(() => {
    register('method');
  }, [register]);

  const handleContinueClick = async (method: AuthMethod) => {
    // If no email is entered and method is email:
    // Trigger all fields to check for other errors
    // Set email error
    if (method === AuthMethod.EMAIL && !getValues(InputName.EMAIL)) {
      await trigger();
      return setError(InputName.EMAIL, { type: 'required', message: content.emailInput.errorMessage });
    }

    setValue('method', method);
    handleSubmit(onFormSubmit)();
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={(e) => e.preventDefault()}>
        <Box>
          {hasMultipleMethods && content.signInOptionsLabel && (
            <Heading as="h3" size="md" mb={4}>
              {content.signInOptionsLabel}
            </Heading>
          )}

          {/* Facebook */}
          {isMethodEnabled(AuthMethod.FACEBOOK) && (
            <>
              <FacebookButton
                isLoading={watchMethod === AuthMethod.FACEBOOK && isRegistering}
                isDisabled={isRegistering}
                onClick={() => handleContinueClick(AuthMethod.FACEBOOK)}>
                {content.buttons.facebook}
              </FacebookButton>
            </>
          )}

          {/* Custom SSO */}
          {isMethodEnabled(AuthMethod.OAUTH) && <>{/* TODO: OAuth */}</>}

          {hasMultipleMethods && isMethodEnabled(AuthMethod.EMAIL) && <OptionsDivider text={content.dividerText} />}

          {/* Email */}
          {isMethodEnabled(AuthMethod.EMAIL) && (
            <Box>
              <Heading as="h3" size="md" mb={4}>
                {content.emailLoginHeadline}
              </Heading>

              {renderInput(content.emailInput)}
              <Button
                isLoading={watchMethod === AuthMethod.EMAIL && isRegistering}
                isDisabled={isRegistering}
                width="full"
                type="button"
                mb={3}
                onClick={() => handleContinueClick(AuthMethod.EMAIL)}>
                {content.continueButton}
              </Button>
            </Box>
          )}
        </Box>
      </form>
    </FormProvider>
  );
};
